import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { fetchIntroduction } from '../firebase/fetcher';
import { QuestionEntity } from '../types';
import { useProgress } from '../contexts/ProgressContext';
import QuestionView from '../components/QuestionView';
import ChoiceList from '../components/ChoiceList';
import ExplanationModal from '../components/ExplanationModal';
import Footer from '../components/Footer';

type Step = 'preface' | 'intro' | 'quiz1' | 'quiz2' | 'quiz3';
const STEPS: Step[] = ['preface', 'intro', 'quiz1', 'quiz2', 'quiz3'];

interface QuizState {
  selected: number | null;
  showModal: boolean;
}

export default function Lesson() {
  const { testIdentifier, lessonIndex } = useParams<{ testIdentifier: string; lessonIndex: string }>();
  const navigate = useNavigate();
  const { markCompleted } = useProgress();

  const [question, setQuestion] = useState<QuestionEntity | null>(null);
  const [loading, setLoading] = useState(true);
  const [step, setStep] = useState<Step>('preface');
  const [quizStates, setQuizStates] = useState<Record<string, QuizState>>({
    quiz1: { selected: null, showModal: false },
    quiz2: { selected: null, showModal: false },
    quiz3: { selected: null, showModal: false },
  });

  useEffect(() => {
    if (!testIdentifier || lessonIndex === undefined) return;
    const keyword = `${testIdentifier}${lessonIndex}`;
    fetchIntroduction(keyword)
      .then(q => setQuestion(q))
      .finally(() => setLoading(false));
  }, [testIdentifier, lessonIndex]);

  function goNext() {
    const idx = STEPS.indexOf(step);
    if (idx < STEPS.length - 1) {
      setStep(STEPS[idx + 1]);
    } else {
      // Lesson complete
      markCompleted(`${testIdentifier}${lessonIndex}`);
      navigate(-1);
    }
  }

  function selectAnswer(quizKey: string, index: number) {
    setQuizStates(prev => ({ ...prev, [quizKey]: { ...prev[quizKey], selected: index } }));
  }

  function toggleModal(quizKey: string, show: boolean) {
    setQuizStates(prev => ({ ...prev, [quizKey]: { ...prev[quizKey], showModal: show } }));
  }

  if (loading) return <div className="page loading">読み込み中...</div>;
  if (!question) return <div className="page loading">データが見つかりません</div>;

  const q = question;

  if (step === 'preface') {
    return (
      <div className="page lesson-page">
        <header className="nav-header">
          <button className="back-btn" onClick={() => navigate(-1)}>‹</button>
          <h1 className="nav-title">この章の目標</h1>
        </header>
        <div className="lesson-content">
          <p className="preface-message">この章の目標は次の問題を解けるようになることです。</p>
          <div className="lesson-chapter-title">{q.title}</div>
          <QuestionView quiz={q.introQuiz} theme={q.introTheme} empha={q.introEmpha} translate={q.introTranslate} />
        </div>
        <Footer onNext={goNext} />
      </div>
    );
  }

  if (step === 'intro') {
    return (
      <div className="page lesson-page">
        <header className="nav-header">
          <button className="back-btn" onClick={() => setStep('preface')}>‹</button>
          <h1 className="nav-title">{q.title}</h1>
        </header>
        <div className="lesson-content scrollable">
          <QuestionView quiz={q.introQuiz} theme={q.introTheme} empha={q.introEmpha} translate={q.introTranslate} />
          <div className="intro-answer"><span className="intro-answer-label">答え：</span>{q.introAnswer}</div>
          <div className="overview-text">{q.overview}</div>
        </div>
        <Footer onNext={goNext} />
      </div>
    );
  }

  // quiz1 / quiz2 / quiz3
  const quizMap = {
    quiz1: { quiz: q.quiz1, theme: q.theme1, empha: q.empha1, translate: q.translate1, answer: q.answer1, comment: q.comment1, choices: q.choice1 },
    quiz2: { quiz: q.quiz2, theme: q.theme2, empha: q.empha2, translate: q.translate2, answer: q.answer2, comment: q.comment2, choices: q.choice2 },
    quiz3: { quiz: q.quiz3, theme: q.theme3, empha: q.empha3, translate: q.translate3, answer: q.answer3, comment: q.comment3, choices: q.choice3 },
  };
  const current = quizMap[step as 'quiz1' | 'quiz2' | 'quiz3'];
  const qState = quizStates[step];
  const result = qState.selected === null ? null : qState.selected === current.answer ? 'correct' : 'wrong';

  return (
    <div className="page lesson-page">
      <header className="nav-header">
        <button className="back-btn" onClick={() => setStep(STEPS[STEPS.indexOf(step) - 1])}>‹</button>
        <h1 className="nav-title">{q.title}</h1>
      </header>
      <div className="lesson-content">
        <QuestionView
          quiz={current.quiz}
          theme={current.theme}
          empha={current.empha}
          translate={current.translate}
          result={result as any}
        />
        <ChoiceList
          choices={current.choices}
          correctIndex={current.answer}
          selectedIndex={qState.selected}
          onSelect={i => selectAnswer(step, i)}
        />
      </div>
      <Footer
        onBack={() => setStep(STEPS[STEPS.indexOf(step) - 1])}
        onComment={qState.selected !== null ? () => toggleModal(step, true) : undefined}
        onNext={qState.selected !== null ? goNext : undefined}
        nextLabel={step === 'quiz3' ? '完了' : '次へ'}
      />
      {qState.showModal && (
        <ExplanationModal comment={current.comment} onClose={() => toggleModal(step, false)} />
      )}
    </div>
  );
}
